


import {
  ChevronRight,
  ViewList as LayoutList
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  Background,
  BackgroundVariant,
  Controls,
  MarkerType,
  MiniMap,
  Panel,
  ReactFlow,
  addEdge,
  reconnectEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FloatingConnectionLine from '../react-flow/edges/FloatingConnectionLine';
import FloatingEdge from '../react-flow/edges/FloatingEdge';

import { ArcEventNode } from './arc-event-node';
import { ArcEventSidebar } from './arc-event-sidebar';
import { CreateEventModal } from './create-event-modal';
import { EventDetailsModal } from './event-details-modal';

import type { Arc, ArcDetails, ArcEdge, ArcEvent, Character, Event, Location } from '@/lib/types';

import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';


interface ArcGraphViewProps {
  arc: Arc;
  details: ArcDetails;
  allEvents: Event[];
  characters: Character[];
  locations: Location[];
  novelId: string;
}

const nodeTypes = {
  arcEvent: ArcEventNode,
};


const edgeTypes = {
  floating: FloatingEdge,
};

export function ArcGraphView({
  arc,
  details,
  allEvents,
  characters,
  locations,
  novelId
}: ArcGraphViewProps) {
  const { backend } = useFirebase();
  const { novel } = useNovelRepository();
  const { toast } = useToast();
  const { screenToFlowPosition } = useReactFlow();
  const { t } = useTranslation();
  const theme = useTheme();

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Modals state
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createPosition, setCreatePosition] = useState<{ x: number; y: number } | undefined>();
  const [createSourceId, setCreateSourceId] = useState<string | null>(null);

  // Handle Edge Deletion
  const handleEdgeDelete = useCallback(
    async (edgeId: string) => {
      if (!novel.id) return;

      const updatedEdges = (details.edges || []).filter((e: ArcEdge) => e.id !== edgeId);

      try {
        await backend.narrative.saveArcDetails(novel.ownerId, novel, arc.id, { edges: updatedEdges });
        setEdges((eds) => eds.filter(e => e.id !== edgeId));
        toast({ title: t('arcs.success'), description: t('arcs.connection_removed') });
      } catch (error) {
        console.error('Error deleting edge:', error);
        toast({ title: t('arcs.error'), description: t('arcs.connection_remove_failed'), variant: 'destructive' });
      }
    },
    [backend, novel, arc.id, details.edges, setEdges, toast, t]
  );

  // Handle Edge Inversion
  const handleEdgeInvert = useCallback(
    async (edgeId: string) => {
      if (!novel.id) return;

      const updatedEdges = (details.edges || []).map((e: ArcEdge) => {
        if (e.id === edgeId) {
          const newSource = e.targetEventId;
          const newTarget = e.sourceEventId;
          return {
            ...e,
            sourceEventId: newSource,
            targetEventId: newTarget,
            id: `edge-${newSource}-${newTarget}`
          };
        }
        return e;
      });

      try {
        await backend.narrative.saveArcDetails(novel.ownerId, novel, arc.id, { edges: updatedEdges });
        // ReactFlow state will be updated by the next initial load useEffect or we can manually update
        // Manual update for immediate UI feedback:
        setEdges((eds) => eds.map(e => {
          if (e.id === edgeId) {
            const newSource = e.target;
            const newTarget = e.source;
            const newId = `edge-${newSource}-${newTarget}`;
            return {
              ...e,
              id: newId,
              source: newSource,
              target: newTarget,
              data: {
                ...e.data,
                onDelete: (id: string) => handleEdgeDelete(id),
                onInvert: (id: string) => handleEdgeInvert(id),
              }
            };
          }
          return e;
        }));
        toast({ title: t('arcs.success'), description: t('arcs.connection_inverted') });
      } catch (error) {
        console.error('Error inverting edge:', error);
        toast({ title: t('arcs.error'), description: t('arcs.connection_invert_failed'), variant: 'destructive' });
      }
    },
    [backend, novel, arc.id, details.edges, setEdges, toast, handleEdgeDelete, t]
  );

  // Sync with Firestore data (Initial load)
  useEffect(() => {
    const initialNodes: Node[] = (details.events || []).map((e: ArcEvent) => {
      const fullEvent = allEvents.find((ae) => ae.id === e.eventId);
      return {
        id: e.eventId,
        type: 'arcEvent',
        position: e.position,
        data: {
          event: e,
          status: fullEvent?.isNarrativelyComplete ? 'complete' : 'notWritten',
          onDetails: (id: string) => {
            setSelectedEventId(id);
            setIsDetailsOpen(true);
          },
        },
      };
    });

    const initialEdges: Edge[] = (details.edges || []).map((e: ArcEdge) => ({
      id: e.id,
      source: e.sourceEventId,
      target: e.targetEventId,
      type: 'floating',
      data: {
        onDelete: (id: string) => handleEdgeDelete(id),
        onInvert: (id: string) => handleEdgeInvert(id),
      },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
      style: { stroke: '#94a3b8', strokeWidth: 2 },
    }));

    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [details, allEvents, setNodes, setEdges, handleEdgeDelete, handleEdgeInvert]);

  // Cleanup: Re-parent edges if an event was deleted from the global events list
  useEffect(() => {
    // Only proceed if we have data and it's not the initial empty state
    if (!novel.id || !allEvents || allEvents.length === 0 || !details.events || details.events.length === 0) return;

    const missingArcEvents = details.events.filter((ae: ArcEvent) => !allEvents.some((e: Event) => e.id === ae.eventId));
    if (missingArcEvents.length === 0) return;

    const missingIds = missingArcEvents.map((ae: ArcEvent) => ae.eventId);


    let updatedEdges = [...(details.edges || [])];

    for (const missingId of missingIds) {
      const incoming = updatedEdges.filter(e => e.targetEventId === missingId);
      const outgoing = updatedEdges.filter(e => e.sourceEventId === missingId);

      // Create bypass connections
      for (const inc of incoming) {
        for (const out of outgoing) {
          const newId = `edge-${inc.sourceEventId}-${out.targetEventId}`;
          if (!updatedEdges.some(e => (e.sourceEventId === inc.sourceEventId && e.targetEventId === out.targetEventId) || e.id === newId)) {
            updatedEdges.push({
              id: newId,
              sourceEventId: inc.sourceEventId,
              targetEventId: out.targetEventId,
            });
          }
        }
      }

      // Remove all edges connected to the missing event
      updatedEdges = updatedEdges.filter(e => e.sourceEventId !== missingId && e.targetEventId !== missingId);
    }

    const updatedEvents = details.events.filter((ae: ArcEvent) => !missingIds.includes(ae.eventId));

    const performCleanup = async () => {
      try {
        await backend.narrative.saveArcDetails(novel.ownerId, novel, arc.id, {
          events: updatedEvents,
          edges: updatedEdges
        });

      } catch (error) {
        console.error('Arc Cleanup Error:', error);
      }
    };

    performCleanup();
  }, [backend, novel, arc.id, details.events, details.edges, allEvents]);

  // Persist Node Position
  const onNodeDragStop = useCallback(
    async (_: unknown, node: Node) => {

      if (!novel.id) return;

      const updatedEvents = (details.events || []).map((e: ArcEvent) =>
        e.eventId === node.id ? { ...e, position: node.position } : e
      );

      try {
        await backend.narrative.saveArcDetails(novel.ownerId, novel, arc.id, { events: updatedEvents });
      } catch (error) {
        console.error('Error saving position:', error);
      }
    },
    [backend, novel, arc.id, details.events]
  );

  // Handle Edge Creation
  const onConnect = useCallback(
    async (connection: Connection) => {
      if (!novel.id) return;

      const newArcEdge: ArcEdge = {
        id: `edge-${connection.source}-${connection.target}`,
        sourceEventId: connection.source,
        targetEventId: connection.target,
      };

      // Replace former edges between these two nodes regardless of direction
      const updatedEdges = [
        ...details.edges.filter((e: ArcEdge) =>
          !((e.sourceEventId === connection.source && e.targetEventId === connection.target) ||
            (e.sourceEventId === connection.target && e.targetEventId === connection.source))
        ),
        newArcEdge
      ];

      try {
        await backend.narrative.saveArcDetails(novel.ownerId, novel, arc.id, { edges: updatedEdges });
        setEdges((eds) => {
          // Manually remove any existing edge in the state too
          const filteredEds = eds.filter(e =>
            !((e.source === connection.source && e.target === connection.target) ||
              (e.source === connection.target && e.target === connection.source))
          );
          return addEdge({
            ...connection,
            id: newArcEdge.id,
            type: 'floating',
            data: {
              onDelete: (id: string) => handleEdgeDelete(id),
              onInvert: (id: string) => handleEdgeInvert(id),
            },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
            style: { stroke: '#94a3b8', strokeWidth: 2 },
          }, filteredEds);
        });
      } catch (error) {
        console.error('Error adding edge:', error);
        toast({ title: t('arcs.error'), description: t('arcs.connection_invert_failed'), variant: 'destructive' });
      }
    },
    [backend, novel, arc.id, details.edges, setEdges, toast, t]
  );

  // Handle drag to empty space
  const onConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const targetIsPane = (event.target as Element)?.classList?.contains('react-flow__pane');

      if (targetIsPane && connectingNodeId.current) {
        const position = screenToFlowPosition({
          x: 'clientX' in event ? event.clientX : event.touches[0].clientX,
          y: 'clientY' in event ? event.clientY : event.touches[0].clientY,
        });

        setCreatePosition(position);
        setCreateSourceId(connectingNodeId.current);
        setIsCreateOpen(true);
      }

      connectingNodeId.current = null;
    },
    [screenToFlowPosition]
  );

  // Handle Edge Reconnection
  const onReconnect = useCallback(
    async (oldEdge: Edge, newConnection: Connection) => {
      if (!novel.id) return;

      const updatedEdges = (details.edges || []).map((e: ArcEdge) =>
        e.id === oldEdge.id
          ? {
            ...e,
            sourceEventId: newConnection.source,
            targetEventId: newConnection.target,
            id: `edge-${newConnection.source}-${newConnection.target}`,
            type: 'floating'
          }
          : e
      );

      try {
        await backend.narrative.saveArcDetails(novel.ownerId, novel, arc.id, { edges: updatedEdges });
        setEdges((els) => reconnectEdge(oldEdge, newConnection, els));
      } catch (error) {
        console.error('Error reconnecting edge:', error);
        toast({ title: t('arcs.error'), description: t('arcs.connection_invert_failed'), variant: 'destructive' });
      }
    },
    [backend, novel, arc.id, details.edges, setEdges, toast, t]
  );

  const connectingNodeId = useRef<string | null>(null);
  const onConnectStart = useCallback((_: unknown, { nodeId }: { nodeId: string | null }) => {
    connectingNodeId.current = nodeId;
  }, []);

  // Handle New Event Success
  const handleCreateSuccess = async (newEvent: ArcEvent, newEdge?: ArcEdge) => {
    if (!novel.id) return;

    const updatedEvents = [...(details.events || []), newEvent];
    if (newEdge) {
      newEdge.id = `edge-${newEdge.sourceEventId}-${newEdge.targetEventId}`;
      // No need to set type here if we use defaultEdgeOptions, but good for clarity
    }
    const updatedEdges = newEdge ? [...(details.edges || []), newEdge] : (details.edges || []);

    try {
      await backend.narrative.saveArcDetails(novel.ownerId, novel, arc.id, {
        events: updatedEvents,
        edges: updatedEdges
      });
      setIsCreateOpen(false);
    } catch (error) {
      console.error('Error updating arc details:', error);
      toast({ title: t('arcs.error'), description: t('arcs.event_add_failed'), variant: 'destructive' });
    }
  };

  // Centralized removal logic
  const handleDetach = useCallback(
    async (eventIds: string[]) => {
      if (!novel.id) return;

      const updatedEvents = (details.events || []).filter((e: ArcEvent) => !eventIds.includes(e.eventId));
      const updatedEdges = (details.edges || []).filter((e: ArcEdge) =>
        !eventIds.includes(e.sourceEventId) && !eventIds.includes(e.targetEventId)
      );

      try {
        await backend.narrative.saveArcDetails(novel.ownerId, novel, arc.id, {
          events: updatedEvents,
          edges: updatedEdges
        });

        if (eventIds.length === 1 && selectedEventId === eventIds[0]) {
          setIsDetailsOpen(false);
          setSelectedEventId(null);
        }

        toast({ title: t('arcs.success'), description: eventIds.length > 1 ? t('arcs.events_detached') : t('arcs.event_detached') });
      } catch (error) {
        console.error('Error removing nodes:', error);
        toast({ title: t('arcs.error'), description: t('arcs.arc_update_failed'), variant: 'destructive' });
      }
    },
    [backend, novel, arc.id, details, selectedEventId, toast, t]
  );

  // Node Removal (standard ReactFlow delete)
  const onNodesDelete = useCallback(
    (deletedNodes: Node[]) => handleDetach(deletedNodes.map(n => n.id)),
    [handleDetach]
  );

  // Drag and drop from sidebar
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();

      const eventId = event.dataTransfer.getData('application/reactflow');
      if (!eventId || !novel.id) return;

      const fullEvent = allEvents.find((e) => e.id === eventId);
      if (!fullEvent) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newArcEvent: ArcEvent = {
        eventId: fullEvent.id,
        name: fullEvent.name,
        summary: fullEvent.shortDescription || '',
        position,
      };

      const updatedEvents = [...(details.events || []), newArcEvent];

      try {
        await backend.narrative.saveArcDetails(novel.ownerId, novel, arc.id, { events: updatedEvents });
        toast({ title: t('arcs.success'), description: t('arcs.event_added') });
      } catch (error) {
        console.error('Error adding dropped event:', error);
        toast({ title: t('arcs.error'), description: t('arcs.event_add_failed'), variant: 'destructive' });
      }
    },
    [backend, novel, arc.id, details.events, allEvents, screenToFlowPosition, toast, t]
  );

  const selectedEvent = useMemo(() =>
    allEvents.find(e => e.id === selectedEventId) || null
    , [allEvents, selectedEventId]);

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', position: 'relative' }}>
      <Box
        sx={{ flex: 1, position: 'relative' }}
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        <ReactFlow
          colorMode={theme.palette.mode}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeDragStop={onNodeDragStop}
          onConnect={onConnect}
          onConnectStart={onConnectStart}
          onConnectEnd={onConnectEnd}
          onReconnect={onReconnect}
          onNodesDelete={onNodesDelete}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          connectionLineComponent={FloatingConnectionLine}
          defaultEdgeOptions={{ type: 'floating' }}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          proOptions={{ hideAttribution: true }}
          edgesReconnectable={true}
        >
          <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="hsl(var(--border))" />
          <Controls />
          <MiniMap />

          <Panel position="top-right">
            <Paper sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9), backdropFilter: 'blur(4px)' }}>
              <Typography variant="caption" fontWeight={700}>{t('arcs.arc_controls')}</Typography>
              <Typography variant="caption" color="text.secondary">
                • {t('arcs.control_drag_nodes')} <br />
                • {t('arcs.control_drag_handles')} <br />
                • {t('arcs.control_drag_empty')} <br />
                • {t('arcs.control_delete_detach')}
              </Typography>
            </Paper>
          </Panel>

          <Panel position="top-left">
            <IconButton
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}
            >
              <LayoutList sx={{ fontSize: 20 }} />
            </IconButton>
          </Panel>
        </ReactFlow>
      </Box>

      {/* Sidebar */}
      <Box
        sx={{
          width: isSidebarOpen ? 300 : 0,
          borderLeft: isSidebarOpen ? 1 : 0,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          transition: 'width 0.3s ease, border 0.3s ease',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <ArcEventSidebar
          events={allEvents}
          existingEventIds={(details.events || []).map((e: ArcEvent) => e.eventId)}
          onCreateNew={() => {
            setCreatePosition(undefined);
            setCreateSourceId(null);
            setIsCreateOpen(true);
          }}
          onViewDetails={(id) => {
            setSelectedEventId(id);
            setIsDetailsOpen(true);
          }}
        />

        <IconButton
          onClick={() => setIsSidebarOpen(false)}
          sx={{
            position: 'absolute',
            top: 12,
            right: 8,
            zIndex: 1,
          }}
          size="small"
        >
          <ChevronRight sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Modals */}
      <EventDetailsModal
        open={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        event={selectedEvent}
        characters={characters}
        locations={locations}
        onDetach={(id) => handleDetach([id])}
      />

      <CreateEventModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        novelId={novelId}
        arcId={arc.id}
        characterId={arc.characterId}
        characterName={characters.find(c => c.id === arc.characterId)?.name}
        onSuccess={handleCreateSuccess}
        initialPosition={createPosition}
        sourceEventId={createSourceId}
      />
    </Box>
  );
}
